import { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FieldError } from '../../../../../components/novarium/NvTableParts';
import useNotify from '../../../../../hooks/useNotify';
import { parseApiError } from '../../../../../services/apiError';
import { formatMoney } from '../../../../../utils/money';
import { getAvailableChecks } from '../../../services/AgendaService';
import { AvailableCheck } from '../../../types/billing.types';
import { errorMessageOf, formatShortDate } from '../billingUtils';

interface CheckPickerDialogProps {
  /** Los que ya están en este cobro: no se ofrecen de nuevo. */
  excludedIds: number[];
  onCancel: () => void;
  onAdd: (checks: AvailableCheck[]) => void;
}

/** "Selección de cheques": cheques de cartera que todavía no están en ningún recibo. */
export default function CheckPickerDialog({ excludedIds, onCancel, onAdd }: CheckPickerDialogProps) {
  const notify = useNotify();
  const [checks, setChecks] = useState<AvailableCheck[] | null>(null);
  const [selected, setSelected] = useState<AvailableCheck[]>([]);
  const [error, setError] = useState<string | undefined>();

  const excludedKey = excludedIds.join(',');
  useEffect(() => {
    let cancelled = false;
    const excluded = excludedKey ? excludedKey.split(',').map(Number) : [];
    getAvailableChecks()
      .then((data) => { if (!cancelled) setChecks(data.filter((c) => !excluded.includes(c.checkId))); })
      .catch((e: unknown) => {
        if (cancelled) return;
        notify.error(errorMessageOf(parseApiError(e), 'No se pudieron cargar los cheques. Intentá de nuevo.'));
        setChecks([]);
      });
    return () => { cancelled = true; };
  }, [excludedKey, notify]);

  const add = () => {
    if (selected.length === 0) {
      setError('Elegí al menos un cheque');
      return;
    }
    onAdd(selected);
  };

  return (
    <Dialog
      header="Selección de cheques"
      visible
      onHide={onCancel}
      className="nv-scope nv-dialog nv-dialog-l"
      modal
      draggable={false}
      footer={(
        <>
          <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onCancel} />
          <Button type="button" className="buttonMain" label="Agregar" onClick={add} />
        </>
      )}
    >
      <div className="nv-dialog-body">
        {checks === null ? (
          <div className="nv-loading" role="status"><ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="4" /> Cargando…</div>
        ) : (
          <>
            <DataTable
              value={checks}
              dataKey="checkId"
              selectionMode="multiple"
              selection={selected}
              onSelectionChange={(e) => { setSelected((e.value as AvailableCheck[]) ?? []); setError(undefined); }}
              emptyMessage="No hay cheques en cartera."
              scrollable
              scrollHeight="420px"
            >
              <Column selectionMode="multiple" className="nv-col-check" />
              <Column header="Número" field="number" />
              <Column header="Banco" field="bank" />
              <Column header="Importe" className="nv-col-money" align="right" alignHeader="right" body={(c: AvailableCheck) => formatMoney(c.amount)} />
              <Column header="Fecha de cobro" className="nv-col-date" body={(c: AvailableCheck) => formatShortDate(c.paymentDate)} />
              <Column header="Estado" body={(c: AvailableCheck) => c.status || '—'} />
              <Column header="Destino" body={(c: AvailableCheck) => c.destination || '—'} />
              <Column header="Descripción" body={(c: AvailableCheck) => c.description || '—'} />
            </DataTable>
            <FieldError id="check-picker-error" message={error} />
          </>
        )}
      </div>
    </Dialog>
  );
}
